import { useEffect, useRef, useState } from "react";

import { AiOutlineClose } from "react-icons/ai";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";

import {
  deletePlaceImage,
  fetchPlaceDetail,
  updatePlaceDetail,
  uploadPlaceImage,
} from "@/apis/admin";
import Button from "@/components/Button";
import ModalLayout from "@/components/Layout/ModalLayout";
import AlertModal from "@/components/Modal/AlertModal";
import useModal from "@/hooks/useModal";
import type { PlaceImage } from "@/types/place";

interface Props {
  plcCd: string;
  initialPlcNm: string;
  initialPlcF: number;
  onClose: () => void;
  onSaved: () => void;
}

const IMAGE_BASE = import.meta.env.VITE_API_BASE_URL || "";

// 서버에 저장된 이미지
interface SavedImage extends PlaceImage {
  _type: "saved";
}

// 로컬에서 추가한 이미지 (아직 업로드 안 됨)
interface PendingImage {
  _type: "pending";
  _id: string; // 임시 ID
  file: File;
  previewUrl: string;
}

type ImageItem = SavedImage | PendingImage;

const PlaceDetailModal = ({ plcCd, initialPlcNm, initialPlcF, onClose, onSaved }: Props) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plcNm, setPlcNm] = useState(initialPlcNm);
  const [plcF, setPlcF] = useState(initialPlcF);
  const [description, setDescription] = useState("");
  const [imageItems, setImageItems] = useState<ImageItem[]>([]);
  const [deletedImgIds, setDeletedImgIds] = useState<number[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const alertModal = useModal();
  const [alertMsg, setAlertMsg] = useState("");

  useEffect(() => {
    loadDetail();
  }, [plcCd]);

  // cleanup preview URLs
  useEffect(() => {
    return () => {
      imageItems.forEach((item) => {
        if (item._type === "pending") URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, []);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const data = await fetchPlaceDetail(plcCd);
      setPlcNm(data.plcNm);
      setPlcF(data.plcF);
      setDescription(data.description || "");
      setImageItems(
        (data.images || []).map((img: PlaceImage) => ({ ...img, _type: "saved" as const })),
      );
      setCurrentIdx(0);
    } catch {
      // API 실패 시 props 초기값 유지
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. 장소 정보 저장
      await updatePlaceDetail({ plcCd, plcNm, plcF, description });

      // 2. 삭제 대기 중인 이미지 삭제
      for (const imgId of deletedImgIds) {
        await deletePlaceImage(imgId);
      }

      // 3. 새로 추가한 이미지 업로드
      const pendingItems = imageItems.filter((item): item is PendingImage => item._type === "pending");
      const errors: string[] = [];
      for (const item of pendingItems) {
        try {
          await uploadPlaceImage(plcCd, item.file);
        } catch (err: any) {
          const msg = getUploadErrorMessage(err, item.file);
          errors.push(`${item.file.name}: ${msg}`);
        }
      }

      if (errors.length > 0) {
        setAlertMsg(`일부 이미지 업로드에 실패했습니다.\n\n${errors.join("\n")}`);
      } else {
        setAlertMsg("수정되었습니다!");
      }
      alertModal.onOpen();
      onSaved();
    } catch {
      setAlertMsg("저장에 실패했습니다.");
      alertModal.onOpen();
    } finally {
      setSaving(false);
    }
  };

  const getUploadErrorMessage = (err: any, file: File): string => {
    const status = err?.response?.status;
    const serverMsg = err?.response?.data?.message;

    if (serverMsg) return serverMsg;

    if (status === 413 || file.size > 5 * 1024 * 1024) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      return `파일 크기(${sizeMB}MB)가 5MB를 초과합니다.`;
    }

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext && !["jpg", "jpeg", "png", "webp"].includes(ext)) {
      return `지원하지 않는 파일 형식(${ext})입니다. JPG, PNG, WebP만 가능합니다.`;
    }

    if (status === 403) return "이미지 업로드 권한이 없습니다.";
    if (status === 500) return "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";

    return "알 수 없는 오류가 발생했습니다.";
  };

  const handleAddFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (imageItems.length >= 3) {
      setAlertMsg("이미지는 최대 3개까지 등록 가능합니다.");
      alertModal.onOpen();
      return;
    }

    // 파일 검증
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !["jpg", "jpeg", "png", "webp"].includes(ext)) {
      setAlertMsg(`지원하지 않는 파일 형식(${ext || "없음"})입니다.\nJPG, PNG, WebP 파일만 업로드 가능합니다.`);
      alertModal.onOpen();
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      setAlertMsg(`파일 크기(${sizeMB}MB)가 너무 큽니다.\n최대 5MB까지 업로드 가능합니다.`);
      alertModal.onOpen();
      return;
    }

    const pending: PendingImage = {
      _type: "pending",
      _id: `pending_${Date.now()}`,
      file,
      previewUrl: URL.createObjectURL(file),
    };

    setImageItems((prev) => [...prev, pending]);
    setCurrentIdx(imageItems.length);
  };

  const handleRemoveImage = (item: ImageItem) => {
    if (item._type === "saved") {
      setDeletedImgIds((prev) => [...prev, item.imgId]);
    } else {
      URL.revokeObjectURL(item.previewUrl);
    }

    const newItems = imageItems.filter((i) =>
      i._type === "saved"
        ? i.imgId !== (item as SavedImage).imgId
        : (i as PendingImage)._id !== (item as PendingImage)._id,
    );
    setImageItems(newItems);
    if (currentIdx >= newItems.length) setCurrentIdx(Math.max(0, newItems.length - 1));
  };

  const getImageSrc = (item: ImageItem): string => {
    if (item._type === "pending") return item.previewUrl;
    return `${IMAGE_BASE}/uploads/places/${plcCd}/${item.fileNm}`;
  };

  const getImageKey = (item: ImageItem): string => {
    if (item._type === "pending") return item._id;
    return String(item.imgId);
  };

  if (loading) {
    return (
      <ModalLayout variant="primary" onClose={onClose}>
        <div className="flex h-40 w-[520px] items-center justify-center text-sm text-gray-400">
          불러오는 중...
        </div>
      </ModalLayout>
    );
  }

  return (
    <ModalLayout variant="primary" onClose={onClose}>
      <div className="flex w-[520px] flex-col sm:w-[600px]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h3 className="text-base font-bold text-gray-900">장소 상세</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <AiOutlineClose size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-4 px-6 py-5">
          {/* 이미지 슬라이더 */}
          <div>
            <div className="relative h-64 overflow-hidden rounded-lg bg-gray-100">
              {imageItems.length > 0 ? (
                <>
                  <div
                    className="flex h-full transition-transform duration-300"
                    style={{ transform: `translateX(-${currentIdx * 100}%)` }}
                  >
                    {imageItems.map((item) => (
                      <div key={getImageKey(item)} className="relative h-64 w-full shrink-0">
                        <img
                          src={getImageSrc(item)}
                          alt={item._type === "saved" ? item.origNm : item.file.name}
                          className="h-full w-full object-contain"
                        />
                        {/* pending 표시 */}
                        {item._type === "pending" && (
                          <span className="absolute left-2 top-2 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-medium text-white">
                            저장 대기
                          </span>
                        )}
                        {/* 삭제 버튼 */}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(item)}
                          className="absolute right-2 top-2 rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-red-500"
                        >
                          <AiOutlineClose size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  {/* 좌우 네비게이션 */}
                  {imageItems.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setCurrentIdx((p) => Math.max(0, p - 1))}
                        className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-1 text-white hover:bg-black/50"
                      >
                        <IoChevronBack size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentIdx((p) => Math.min(imageItems.length - 1, p + 1))}
                        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-1 text-white hover:bg-black/50"
                      >
                        <IoChevronForward size={16} />
                      </button>
                    </>
                  )}
                  {/* 도트 인디케이터 */}
                  {imageItems.length > 1 && (
                    <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
                      {imageItems.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setCurrentIdx(i)}
                          className={`h-2 w-2 rounded-full transition-colors ${
                            i === currentIdx ? "bg-white" : "bg-white/50"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-gray-400">
                  등록된 이미지가 없습니다
                </div>
              )}
            </div>
            {/* 썸네일 + 드래그 순서 변경 */}
            {imageItems.length > 0 && (
              <div className="mt-2 flex items-center gap-2">
                {imageItems.map((item, i) => (
                  <div
                    key={getImageKey(item)}
                    draggable
                    onDragStart={() => setDragIdx(i)}
                    onDragOver={(e) => { e.preventDefault(); setDragOverIdx(i); }}
                    onDragLeave={() => setDragOverIdx(null)}
                    onDrop={() => {
                      if (dragIdx !== null && dragIdx !== i) {
                        const arr = [...imageItems];
                        const [moved] = arr.splice(dragIdx, 1);
                        arr.splice(i, 0, moved);
                        setImageItems(arr);
                        setCurrentIdx(i);
                      }
                      setDragIdx(null);
                      setDragOverIdx(null);
                    }}
                    onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                    className={`group relative cursor-grab active:cursor-grabbing ${
                      dragOverIdx === i && dragIdx !== i ? "scale-110" : ""
                    } transition-transform`}
                  >
                    <button
                      type="button"
                      onClick={() => setCurrentIdx(i)}
                      className={`relative h-14 w-14 overflow-hidden rounded-md border-2 transition-all ${
                        i === currentIdx ? "border-primary shadow-md" : "border-gray-200 hover:border-primary/50"
                      } ${dragIdx === i ? "opacity-40" : ""}`}
                    >
                      <img
                        src={getImageSrc(item)}
                        alt=""
                        className="h-full w-full object-cover pointer-events-none"
                      />
                      {/* 번호 뱃지 */}
                      <span className="absolute left-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-white">
                        {i + 1}
                      </span>
                    </button>
                    {/* 화살표 순서 변경 (드래그 불가능한 환경 대비) */}
                    {imageItems.length > 1 && (
                      <div className="absolute -bottom-5 left-1/2 flex -translate-x-1/2 gap-0.5">
                        {i > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              const arr = [...imageItems];
                              [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                              setImageItems(arr);
                              setCurrentIdx(i - 1);
                            }}
                            className="rounded px-0.5 text-[10px] text-gray-400 hover:text-primary"
                          >
                            ◀
                          </button>
                        )}
                        {i < imageItems.length - 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const arr = [...imageItems];
                              [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
                              setImageItems(arr);
                              setCurrentIdx(i + 1);
                            }}
                            className="rounded px-0.5 text-[10px] text-gray-400 hover:text-primary"
                          >
                            ▶
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* 이미지 추가 버튼 */}
            {imageItems.length < 3 && (
              <div className={imageItems.length > 0 ? "mt-6" : "mt-2"}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleAddFile}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 transition-colors hover:border-primary/50 hover:text-primary"
                >
                  {`+ 이미지 추가 (${imageItems.length}/3)`}
                </button>
                <span className="ml-2 text-xs text-gray-400">
                  JPG, PNG, WebP / 최대 5MB
                </span>
              </div>
            )}
          </div>

          {/* 폼 */}
          <div className="flex flex-col gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">장소명</label>
              <input
                type="text"
                value={plcNm}
                onChange={(e) => setPlcNm(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">층</label>
              <input
                type="number"
                value={plcF}
                onChange={(e) => setPlcF(Number(e.target.value))}
                className="h-10 w-24 rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">설명 / 주의사항</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="장소에 대한 설명이나 주의사항을 입력하세요"
                className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <Button
            variant="custom"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            취소
          </Button>
          <Button
            variant="custom"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? "저장 중..." : "저장"}
          </Button>
        </div>
      </div>

      {alertModal.isOpen && (
        <AlertModal onClose={() => { alertModal.onClose(); if (alertMsg === "수정되었습니다!") onClose(); }}>
          <span className="whitespace-pre-line">{alertMsg}</span>
        </AlertModal>
      )}
    </ModalLayout>
  );
};

export default PlaceDetailModal;
